import { UAParser } from 'ua-parser-js';
import type { Event } from '@sentry/vue';

export const ignoredStatusCodes = [400, 401, 403, 404, 422, 425, 429];

export const ignoredErrorMessages = [
  'ReportingObserver [deprecation]',
  'Request aborted',
  'timeout of 30000ms exceeded',
  'timeout exceeded',
  'Network Error',
  'Request failed with empty response',
  'Load failed', // XXX: Ignore iOS cryptic failed fetch response, as we don't get any more details on why the request failed
  'CanceledError',
  'Blocked a frame with origin',
  'Timeout',
  'Bad request',
  'has not joined to the Referral Program yet',
  'Event `ErrorEvent` captured as exception with message `Script error.`',
  'Can\'t find variable: gmo', // Known issue: https://issuetracker.google.com/issues/396043331?pli=1
  'NotSupportedError',
  'Rejected',
  'Maximum call stack size exceeded', //temporally ignored
  /Object Not Found Matching Id:\d+, MethodName:update/, // unactionable error from CEFSharp, a .NET library that embeds chromium in .NET apps
];

export const isOldBrowsers = () => {
  const parser = new UAParser(navigator.userAgent);
  const browser = parser.getBrowser();

  if (browser.name === 'Chrome' && browser.major < 109) {
    return true;
  }
  if (browser.name === 'Firefox' && browser.major < 54) {
    return true;
  }
  if (browser.name === 'Safari' && browser.major < 10) {
    return true;
  }
  if (browser.name === 'Opera' && browser.major < 38) {
    return true;
  }
  return false;
};

export const denyUrls = [
  /^safari-extension:\/\//i,
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  // Recaptcha
  /recaptcha\/releases/i,
];

/**
 * Sentry stores an exception chain oldest-first, so a linked `cause` is *prepended* and
 * values[0] is no longer the thrown error. Scan every entry rather than assuming index 0.
 */
const hasExceptionValue = (event: Event, needle: string): boolean =>
  (event?.exception?.values ?? []).some(value => (value.value ?? '').includes(needle));

/** The thrown error itself: the last entry of the chain, never one of its causes. */
const primaryExceptionValue = (event: Event): string => {
  const values = event?.exception?.values ?? [];

  return values[values.length - 1]?.value ?? '';
};

export const filterUnwantedExceptions = (event: Event, originalException?: unknown) => {
  if (isOldBrowsers()) return null;
  // @see https://github.com/getsentry/sentry-javascript/issues/2514#issuecomment-603971338
  const isBrokenTimeoutError = originalException === 'Timeout';
  //Check if the browser is an IE, and if so, return null
  const isInternetExplorer = /MSIE|Trident.*rv:11\./.test(window.navigator.userAgent);
  const response = originalException && (originalException as any).response;

  const isIgnoreByStatuses = response && response.status && ignoredStatusCodes.includes(response.status) || false;

  if (isInternetExplorer || isBrokenTimeoutError || isIgnoreByStatuses) {
    return null;
  }
  const isInstagramWebView = /Instagram/.test(window.navigator.userAgent);

  const { exception } = event ?? {};

  const autofillCallbackHandlerError = hasExceptionValue(event, '_AutofillCallbackHandler');
  // check if user agent is Instagram and autofillCallbackHandlerError, if so, return null

  if (isInstagramWebView && autofillCallbackHandlerError) {
    return null;
  }

  // Known unactionable noise from Sentry Session Replay's shadow DOM patching
  const isFromReplayShadowDom = (exception?.values ?? []).some(
    (value) =>
      (value.value ?? '').includes("Cannot read properties of undefined (reading 'prototype')") &&
      (value.stacktrace?.frames ?? []).some(
        (frame) =>
          frame.filename?.includes('@sentry/replay') &&
          frame.function?.includes('ShadowDomManager.patchAttachShadow'),
      ),
  );
  if (isFromReplayShadowDom) {
    return null;
  }

  // We want to remove specific error `Cannot read properties of null` that generates by gpay,
  // Because we can't fix it on out side and it not impacted logic. But we should not remove
  // all errors like this.
  // Checked on the thrown error only: this branch drops anything that is not the
  // gpay case, so matching a linked cause would discard a real error wrapping it.
  if (primaryExceptionValue(event).includes('Non-Error exception captured with keys')) {
    try {
      const { statusCode, stack, statusMessage } = event.extra.__serialized__.error;
      const isDeveloperError = statusCode === 'DEVELOPER_ERROR';
      const isGooglePayNonErrorException = stack.indexOf('gp/p/js/pay.js') !== -1;
      const isAccessToNullNonErrorException = statusMessage.indexOf('Cannot read properties of null') !== -1;

      if (!isGooglePayNonErrorException) {
        return null;
      }

      if (isGooglePayNonErrorException && !isDeveloperError) {
        return null;
      }
      if (isGooglePayNonErrorException && isAccessToNullNonErrorException) {
        return null;
      }

    } catch (e) { /* empty */ }

  } else if (exception === undefined) {
    return event;
  } else {
    try {
      const primaryException = exception.values[exception.values.length - 1];
      const firstFrame = primaryException.stacktrace.frames[0];
      const isGooglePay = firstFrame.filename.indexOf('gp/p/js/pay.js') !== -1;
      const isAccessToNull = event.message.indexOf('Cannot read properties of null') !== -1;

      if (isGooglePay && isAccessToNull) {
        return null;
      }

    } catch (e) { /* empty */ }
  }

  return event;
};
